import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import useUnsavedWarning from '../../../hooks/useUnsavedWarning';
import Button from "../../../components/Button";
import TextInput from "../../../components/TextInput";
import { confirmDialog } from "../../../utils/confirm";
import { Plus, Trash2 } from "lucide-react";
import { getFaqs, saveFaqs, mapFaqFromApi } from "../../../api";
import PageLoader from "../../../components/PageLoader";
import Swal from "sweetalert2";

const toStoredItem = (item, index) => ({
  id: item.id ?? `faq-${index}`,
  question: item.question ?? "",
  answer: item.answer ?? "",
});

const AdminCMSFAQ = () => {
  const [items, setItems] = useState([]);
  const [originalItems, setOriginalItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Validation & Change tracking
  const isFormInvalid = useMemo(() => {
    if (items.length === 0) return false;
    return items.some((item) => !item.question?.trim() || !item.answer?.trim());
  }, [items]);

  const isChanged = useMemo(() => {
    if (items.length !== originalItems.length) return true;
    return items.some((item, i) => {
      const orig = originalItems[i];
      return item.question !== orig.question || item.answer !== orig.answer;
    });
  }, [items, originalItems]);
  useUnsavedWarning(isChanged);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const apiItems = await getFaqs();
        if (!mounted) return;
        const data = (Array.isArray(apiItems) ? apiItems : []).map((x, i) =>
          toStoredItem(mapFaqFromApi(x, i), i),
        );
        setItems(JSON.parse(JSON.stringify(data)));
        setOriginalItems(JSON.parse(JSON.stringify(data)));
      } catch (err) {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: err.response?.data?.message || err.message || "Failed to load FAQs",
          confirmButtonColor: "#38915c",
        });
        setItems([]);
        setOriginalItems([]);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  const addItem = () => {
    setItems([...items, { id: `faq-${Date.now()}`, question: "", answer: "" }]);
  };

  const updateItem = (index, field, value) => {
    const next = [...items];
    next[index] = { ...next[index], [field]: value };
    setItems(next);
  };

  const removeItem = async (index) => {
    const confirmed = await confirmDialog({
      title: "Remove this FAQ?",
      text: "It will be removed from the homepage FAQ section.",
    });
    if (!confirmed) return;
    setItems(items.filter((_, i) => i !== index));
    Swal.fire({
      icon: "success",
      title: "Item Removed",
      text: "FAQ removed successfully",
      confirmButtonColor: "#38915c",
      timer: 2000,
      showConfirmButton: false,
    });
  };

  const handleSave = async () => {
    if (isFormInvalid) {
      Swal.fire({
        icon: "warning",
        title: "Incomplete FAQ",
        text: "Please fill in all questions and answers before saving.",
        confirmButtonColor: "#38915c",
      });
      return;
    }

    setSaving(true);
    try {
      const saved = await saveFaqs(items);
      const data = Array.isArray(saved) ? saved : items;
      setItems(JSON.parse(JSON.stringify(data)));
      setOriginalItems(JSON.parse(JSON.stringify(data)));
      Swal.fire({
        icon: "success",
        title: "Saved",
        text: "FAQ updated successfully",
        confirmButtonColor: "#38915c",
      });
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text:
          err.response?.data?.message || err.message || "Failed to save FAQs",
        confirmButtonColor: "#38915c",
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <PageLoader />;
  }

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div>
          <Link
            to="/admin/cms"
            className="text-sm text-primary-600 hover:underline mb-1 inline-block"
          >
            ← CMS Pages
          </Link>
          <h1 className="text-2xl font-bold text-gray-800">FAQ</h1>
          <p className="text-gray-600 text-sm mt-1">
            Manage frequently asked questions. All fields are required.
          </p>
        </div>
      </div>



      <div className="space-y-6">
        {items.length === 0 && (
          <div className="p-6 rounded-xl border-2 border-dashed border-gray-200 bg-white text-gray-600 text-center">
            FAQ is not available
          </div>
        )}
        {items.map((item, index) => (
          <div
            key={item.id}
            className="p-6 rounded-xl border-2 border-gray-200 bg-white"
          >
            <div className="flex items-center justify-between gap-2 mb-4">
              <span className="text-sm font-medium text-gray-500">
                FAQ {index + 1}
              </span>
              <button
                type="button"
                onClick={() => removeItem(index)}
                className="p-2 text-danger-600 hover:bg-danger-50 rounded-lg"
                title="Remove FAQ"
              >
                <Trash2 size={18} />
              </button>
            </div>
            <div className="grid gap-4">
              <TextInput
                label="Question"
                value={item.question || ""}
                onChange={(e) => updateItem(index, "question", e.target.value)}
                placeholder="e.g. How do I access courses after payment?"
                error={!item.question?.trim() && "Question is required"}
                required
              />
              <TextInput
                label="Answer"
                value={item.answer || ""}
                onChange={(e) => updateItem(index, "answer", e.target.value)}
                placeholder="Answer text"
                error={!item.answer?.trim() && "Answer is required"}
                multiline
                rows={4}
                required
              />
            </div>
          </div>
        ))}
        <div className="flex gap-4 mt-8 items-stretch">
          <button
            type="button"
            onClick={addItem}
            className="w-[70%] flex items-center justify-center gap-2 py-4 rounded-xl border-2 border-dashed border-gray-300 text-gray-600 hover:border-primary-400 hover:text-primary-600 hover:bg-primary-50/50 transition-colors"
          >
            <Plus size={20} />
            Add FAQ
          </button>
          <div className="w-[30%]">
            <Button
              variant="primary"
              onClick={handleSave}
              className="w-full !h-full shadow-md"
              disabled={saving || !isChanged || isFormInvalid}
            >
              {saving ? "Saving..." : "Save Changes"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminCMSFAQ;
